import { Component, forwardRef, Input } from '@angular/core';
import { PlatformApiService } from '../../services/platform-api.service';
import { SessionService } from 'src/app/services/session.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Account, Brand, RoleType} from '../../models/AccountModel';
import { WizardScreenComponent } from 'src/app/shared/wizard.component';

@Component({
  selector: 'cw-ci-account-report-branding',
  templateUrl: './account-report-branding.component.html',
  styleUrls: ['./account-report-branding.component.scss'],
  providers: [{provide: WizardScreenComponent, useExisting: forwardRef(() => AccountReportBrandingComponent)}]
})
export class AccountReportBrandingComponent extends WizardScreenComponent {
  @Input()
  account: Account;
  _brand: Brand = new Brand();
  RoleType = RoleType;

  set brand(value: Brand) {
    console.log(value);
    if (value && !value.name) {
      this.loadDefaultBrand();
      return;
    }
    this._brand = value;
    this.form.reset();
    this.form.controls.baseColor.setValue(this._brand.baseColor);
    this.form.controls.highlightColor.setValue(this._brand.highlightColor);
    this.form.controls.textBackgroundColor.setValue(this._brand.textBackgroundColor);
    this.form.controls.documentTitlePage.setValue(this._brand.documentTitlePage);
    this.form.controls.documentPageHeader.setValue(this._brand.documentPageHeader);
  }

  constructor(public _sessionService: SessionService, private _apiService: PlatformApiService) {
    super();
    this.form = new FormGroup({
      baseColor: new FormControl('', [Validators.required]),
      highlightColor: new FormControl('', [Validators.required]),
      textBackgroundColor: new FormControl('', [Validators.required]),
      documentTitlePage: new FormControl('', [Validators.required]),
      documentPageHeader: new FormControl('', [Validators.required])
    });
  }

  onLoad() {
    this.showSpinner()
    // This will get the brand associated with the account or the default brand, currently 'Sienna'
    this._apiService.getAccountBrand(this.account.id, undefined).subscribe(
      resp => {
        this.brand = resp;
        if (this._brand.name === 'sienna' || this._brand.id === 1) {
          // This is a clone of the default brand, so change name and id from default
          this._brand.name = this.account.name;
          this._brand.id = null;
        }
        super.onLoad();
        this.hideSpinner();
      }
    );
  }

  onSave() {
    if (this.form.valid) {
      this.showSpinner('Saving...');

      const json = Object.assign({}, this._brand, this.form.value);
      if (this.account) {
        // This saves for brand associated with an msp account
        this._apiService.saveAccountBrand(this.account.id, this._brand.id, { brand: json }).subscribe(
          resp => {
            if (this._brand.id !== resp.id) {
              // The account is updated by the api call
              this.account.brandingId = resp.id;
            }
            this._brand = resp;
            super.onSave();
            this.hideSpinner();
          }, err => {
            this.hideSpinner();
            if(err.status == 400 && err.error.errors.length > 0){
              err.error.errors.forEach(error => {
                if(error.field == 'documentTitlePage') {
                  this.form.controls.documentTitlePage.setErrors({invalid: true})
                }
                if(error.field == 'documentPageHeader') {
                  this.form.controls.documentPageHeader.setErrors({invalid: true})
                }
              });
            }
          }
        );
      }
      else {
        // This saves for admin brands
        this._apiService.saveBrand(this._brand.id, { brand: json }).subscribe(
          resp => {
            this._brand = resp;
            this.hideSpinner()
          }, err => {
            this.hideSpinner();
            if(err.status == 400 && err.error.errors.length > 0){
              err.error.errors.forEach(error => {
                if(error.field == 'documentTitlePage') {
                  this.form.controls.documentTitlePage.setErrors({invalid: true})
                }
                if(error.field == 'documentPageHeader') {
                  this.form.controls.documentPageHeader.setErrors({invalid: true})
                }
              });
            }
          }
        );
      }
    }
  }

  /*
   * colorChange - Called when the color picker changes color
  */
  colorChange(colorName, color) {
    this.form.controls[colorName].setValue(color);
    this.form.controls[colorName].markAsDirty();
  }

  /*
   * imageChange - Called when an image is selected from the file chooser
  */
 imageChange(name, event) {
  if (name === 'Title') {
    name = 'documentTitlePage';
  } else if (name === 'Content') {
    name = 'documentPageHeader';
  }

  if (event.target.files && event.target.files[0]) {
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.onloadend = (e) => {
      this._brand[name] = reader.result;
      this.form.controls[name].setValue(this._brand[name]);
      this.form.controls[name].markAsDirty();
      event.target.value = '';
    };
  }
}

  loadDefaultBrand(spinnerMessage?: string) {
    if (!spinnerMessage) {
      this.showSpinner();
    }
    this._apiService.getAccountBrand(this.account.id, undefined).subscribe(resp => {
      this._brand = resp;
      this._brand.name = this.account.name;
      this._brand.id = null;
      this.brand = this._brand;
      if (this.account.id == this._sessionService.getCurrentAccount().id) {
        this._sessionService.updateWorkingAccount(this.account);
      }
      if (spinnerMessage) {
        this.showMessage(spinnerMessage);
      } else {
        this.hideSpinner();
      }
    });
  }

  revertToDefault() {
    this.showPrompt('You are about to remove your custom branding. If you continue, you will have to upload your custom branding files again to re-enable it. Do you want to continue?',(shouldRevert) => {
      if(shouldRevert) {
        if (this.account) {
          this.showSpinner('Reverting to the default brand.');
        } else {
          this.showSpinner('Deleting the custom brand.');
        }

        if (this.account) {
          // This saves for brand associated with an msp account
          this._apiService.deleteAccountBrand(this.account.id, this._brand.id).subscribe(
            resp => {
              this.account.brandingId = undefined;
              if (this.account) {
                this.loadDefaultBrand('Custom brand has been removed.');
              }
            }
          );
        }
        else {
          // This saves for admin brands
          this._apiService.deleteBrand(this._brand.id).subscribe(
            resp => {
              if (this.account) {
                this.loadDefaultBrand('Custom brand has been removed.');
              }
            }
          );
        }
      }
    });
  }
}